import { Map } from 'immutable';
import { uniq } from 'ramda';
import { createReducer } from 'redux-act';

import { getEntityFromMap } from '@/utils/entityUtils';

import Layout from '../../models/interfaces/layout/Layout';
import MergedTable from '../../models/interfaces/layout/MergedTable';
import Table from '../../models/interfaces/layout/Table';
import TableType from '../../models/interfaces/layout/TableType';
import { authenticationRequired } from '../actions/authentication/authenticationRequired';
import { logout } from '../actions/authentication/logout';
import { layoutCreated, LayoutCreatedPayload } from '../actions/layout/createLayout';
import { layoutDeleted, LayoutDeletedPayload } from '../actions/layout/deleteLayout';
import { layoutsFetched, LayoutsFetchedPayload } from '../actions/layout/fetchLayouts';
import { layoutSaved, LayoutSavedPayload } from '../actions/layout/saveLayout';
import { reservationAccepted } from '../actions/reservation/acceptReservation';
import { reservationCreated } from '../actions/reservation/createReservation';
import { reservationResolved } from '../actions/reservation/resolveReservation';
import { reservationUpdated } from '../actions/reservation/updateReservation';

export interface LayoutEntityArrays {
  layouts: Map<string, Layout>;
  mergedTables: Map<string, MergedTable>;
  tables: Map<string, Table>;
  tableTypes: Map<string, TableType>;
}

export interface LayoutState {
  entities: LayoutEntityArrays;
  displayedLayout: string | null;
}

const defaultState: LayoutState = {
  entities: {
    layouts: Map<string, Layout>(),
    mergedTables: Map<string, MergedTable>(),
    tables: Map<string, Table>(),
    tableTypes: Map<string, TableType>(),
  },
  displayedLayout: null,
};

const layoutReducer = createReducer<LayoutState>({}, defaultState);

layoutReducer.on(layoutsFetched, (layoutState, { placeUuid, layouts, mergedTables, tables, tableTypes }: LayoutsFetchedPayload): LayoutState => {
  const layoutUuids = Array.from(layoutState.entities.layouts.filter((layout) => layout.placeUuid === placeUuid).keys());

  return {
    displayedLayout: layouts.length > 0 ? layouts[0].uuid : null,
    entities: {
      layouts: layoutState.entities.layouts.filter((layout) => layout.placeUuid !== placeUuid).merge(Map(layouts.map((layout) => [layout.uuid, layout]))),
      mergedTables: layoutState.entities.mergedTables
        .filter((mergedTable) => !layoutUuids.includes(mergedTable.layoutUuid))
        .merge(Map(mergedTables.map((mergedTable) => [mergedTable.uuid, mergedTable]))),
      tables: layoutState.entities.tables.filter((table) => !layoutUuids.includes(table.layoutUuid)).merge(Map(tables.map((table) => [table.uuid, table]))),
      tableTypes: layoutState.entities.tableTypes
        .filter((tableType) => !layoutUuids.includes(tableType.layoutUuid))
        .merge(Map(tableTypes.map((tableType) => [tableType.uuid, tableType]))),
    },
  };
});

layoutReducer.on(layoutDeleted, (layoutState, payload: LayoutDeletedPayload): LayoutState => {
  const { layoutUuid } = payload;
  return {
    ...layoutState,
    entities: {
      layouts: layoutState.entities.layouts.filter((layout) => layout.uuid !== layoutUuid),
      mergedTables: layoutState.entities.mergedTables.filter((mergedTable) => mergedTable.layoutUuid !== layoutUuid),
      tables: layoutState.entities.tables.filter((table) => table.layoutUuid !== layoutUuid),
      tableTypes: layoutState.entities.tableTypes.filter((tableType) => tableType.layoutUuid !== layoutUuid),
    },
  };
});

layoutReducer.on(layoutSaved, (layoutState, payload: LayoutSavedPayload): LayoutState => {
  const { layout, mergedTables, tables, tableTypes } = payload;
  const { uuid: layoutUuid } = layout;
  return {
    ...layoutState,
    entities: {
      layouts: layoutState.entities.layouts.set(layout.uuid, layout),
      mergedTables: layoutState.entities.mergedTables
        .filter((mergedTable) => mergedTable.layoutUuid !== layoutUuid)
        .merge(Map(mergedTables.map((mergedTable) => [mergedTable.uuid, mergedTable]))),
      tables: layoutState.entities.tables.filter((table) => table.layoutUuid !== layoutUuid).merge(Map(tables.map((table) => [table.uuid, table]))),
      tableTypes: layoutState.entities.tableTypes
        .filter((tableType) => tableType.layoutUuid !== layoutUuid)
        .merge(Map(tableTypes.map((tableType) => [tableType.uuid, tableType]))),
    },
  };
});

layoutReducer.on(layoutCreated, (layoutState, payload: LayoutCreatedPayload): LayoutState => {
  const { layout, mergedTables, tables, tableTypes } = payload;
  return {
    ...layoutState,
    entities: {
      layouts: layoutState.entities.layouts.set(layout.uuid, layout),
      mergedTables: layoutState.entities.mergedTables.merge(Map(mergedTables.map((mergedTable) => [mergedTable.uuid, mergedTable]))),
      tables: layoutState.entities.tables.merge(Map(tables.map((table) => [table.uuid, table]))),
      tableTypes: layoutState.entities.tableTypes.merge(Map(tableTypes.map((tableType) => [tableType.uuid, tableType]))),
    },
  };
});

function addMergedTableToState(state: LayoutState, createdMergedTable: MergedTable | null): LayoutState {
  if (!createdMergedTable) {
    return state;
  }

  const layout = getEntityFromMap(state.entities.layouts, createdMergedTable.layoutUuid);
  const tables = createdMergedTable.tableUuids.map((tableUuid) => getEntityFromMap(state.entities.tables, tableUuid));
  const entities = {
    ...state.entities,
    layouts: state.entities.layouts.set(layout.uuid, {
      ...layout,
      mergedTableUuids: uniq([...(layout.mergedTableUuids ?? []), createdMergedTable.uuid]),
    }),
    mergedTables: state.entities.mergedTables.set(createdMergedTable.uuid, createdMergedTable),
    tables: tables.reduce(
      (tableMap, table) =>
        tableMap.set(table.uuid, {
          ...table,
          mergedTableUuids: uniq([...(table.mergedTableUuids ?? []), createdMergedTable.uuid]),
        }),
      state.entities.tables
    ),
  };
  return { ...state, entities };
}

layoutReducer.on(reservationCreated, (state, { createdMergedTable }): LayoutState => addMergedTableToState(state, createdMergedTable));

layoutReducer.on(reservationUpdated, (state, { createdMergedTable }): LayoutState => addMergedTableToState(state, createdMergedTable));

layoutReducer.on(reservationResolved, (state, { createdMergedTable }): LayoutState => addMergedTableToState(state, createdMergedTable));

layoutReducer.on(reservationAccepted, (state, { createdMergedTable }): LayoutState => addMergedTableToState(state, createdMergedTable));

layoutReducer.on(logout, () => defaultState);

layoutReducer.on(authenticationRequired, () => defaultState);

export default layoutReducer;
